<template>
  <v-dialog v-model="dialog" persistent max-width="800">
    <v-card>
      <v-card-title class="kb-modal-title">
        <h3>{{ isEdit ? "Editar Pergunta" : "Nova Pergunta" }}</h3>
        <v-btn icon @click="close">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="kb-modal-body">
        <div class="kb-form">
        <div class="kb-field">
          <label>Pergunta *</label>
          <InputText
            type="text"
            place-holder="Escreva a pergunta"
            v-model="form.question"
            labelCharLimit="500"
            width="100%"
          />
        </div>

        <div class="kb-field">
          <label>Resposta *</label>
          <vue-editor
            v-model="form.answer"
            :editor-toolbar="customToolbar"
            placeholder="Escreva a resposta"
            @ready="onAnswerEditorReady"
          />
          <div class="kb-variables">
            <span class="kb-variables-label">Inserir variável:</span>
            <v-chip
              v-for="variable in dynamicVariables"
              :key="variable.token"
              small
              outline
              color="primary"
              :title="variable.hint"
              @click="insertVariable(variable.token)"
            >
              {{ variable.label }}
            </v-chip>
            <span class="kb-variables-hint">
              O valor é resolvido por campanha na exibição (ex.: 6% ou 7%).
            </span>
          </div>
        </div>

        <div class="kb-field-row">
          <div class="kb-field">
            <label>Categoria *</label>
            <v-select
              :items="categories"
              v-model="form._categoryId"
              item-text="name"
              item-value="id"
              placeholder="Selecione a categoria"
              hide-details
              @change="onCategoryChange"
            />
          </div>

          <div class="kb-field">
            <label>Subcategoria *</label>
            <v-select
              :items="subcategoryOptions"
              v-model="form.subcategoryId"
              item-text="name"
              item-value="id"
              :placeholder="form._categoryId ? 'Selecione a subcategoria' : 'Selecione a categoria primeiro'"
              :disabled="!form._categoryId"
              hide-details
            />
          </div>
        </div>

        <div class="kb-field">
          <label>Tipos de campanha *</label>
          <v-select
            :items="campaignTypeOptions"
            v-model="form.faqCampaignTypeIds"
            item-text="label"
            item-value="value"
            multiple
            chips
            placeholder="Selecione um ou mais tipos"
          />
        </div>

        <div class="kb-field">
          <v-checkbox
            v-model="form.isVisibleToDonor"
            label="Visível para o doador (caso contrário, somente IA acessa)"
          />
        </div>

        <div class="kb-field">
          <label>Ordem</label>
          <InputText
            type="number"
            v-model="form.sortOrder"
            place-holder="Ordem de exibição (opcional)"
            width="100%"
          />
        </div>
        </div>
      </v-card-text>

      <v-card-actions class="kb-modal-actions">
        <v-btn flat round @click="close">Cancelar</v-btn>
        <v-btn
          flat
          round
          color="white"
          class="btn-primary px-4"
          :disabled="!canSave"
          @click="save"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script type="plain/text">
import KnowledgeBaseFaqService from "@/scripts/services/knowledgeBaseFaq.service";
import { FaqCampaignTypeOptions } from "@/scripts/models/enums/faqCampaignType.enum";
import { FaqDynamicVariables } from "@/scripts/models/faqDynamicVariables";
import { VueEditor } from "vue2-editor";

const emptyForm = () => ({
  id: 0,
  question: "",
  answer: "",
  isVisibleToDonor: true,
  sortOrder: null,
  faqCampaignTypeIds: [],
  subcategoryId: null,
  _categoryId: null
});

export default {
  components: {
    VueEditor
  },
  props: {
    // Categorias (com subcategorias aninhadas) para classificar a pergunta.
    categories: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    dialog: false,
    isEdit: false,
    form: emptyForm(),
    campaignTypeOptions: FaqCampaignTypeOptions,
    dynamicVariables: FaqDynamicVariables,
    answerEditor: null,
    knowledgeBaseFaqService: new KnowledgeBaseFaqService(),
    customToolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"]
    ]
  }),
  computed: {
    subcategoryOptions() {
      if (!this.form._categoryId) return [];
      const category = this.categories.find(c => c.id === this.form._categoryId);
      return category ? category.subcategories || [] : [];
    },
    canSave() {
      return (
        this.form.question &&
        this.form.question.trim().length > 0 &&
        this.form.answer &&
        this.form.answer.trim().length > 0 &&
        this.form.faqCampaignTypeIds &&
        this.form.faqCampaignTypeIds.length > 0 &&
        !!this.form.subcategoryId
      );
    }
  },
  methods: {
    // open(faq) -> edição; open(null, { subcategoryId, campaignType }) -> criação pré-classificada.
    open(faq, options = {}) {
      if (faq) {
        this.isEdit = true;
        this.form = {
          id: faq.id,
          question: faq.question,
          answer: faq.answer,
          isVisibleToDonor: faq.isVisibleToDonor,
          sortOrder: faq.sortOrder,
          faqCampaignTypeIds: faq.faqCampaignTypeIds ? faq.faqCampaignTypeIds.slice() : [],
          subcategoryId: faq.subcategoryId || null,
          _categoryId: this.findCategoryIdBySubcategory(faq.subcategoryId)
        };
      } else {
        this.isEdit = false;
        this.form = emptyForm();
        if (options.subcategoryId) {
          this.form.subcategoryId = options.subcategoryId;
          this.form._categoryId = this.findCategoryIdBySubcategory(options.subcategoryId);
        }
        if (options.campaignType) {
          this.form.faqCampaignTypeIds = [options.campaignType];
        }
      }
      this.dialog = true;
    },
    findCategoryIdBySubcategory(subcategoryId) {
      if (!subcategoryId) return null;
      const category = this.categories.find(
        c => (c.subcategories || []).some(s => s.id === subcategoryId)
      );
      return category ? category.id : null;
    },
    onCategoryChange() {
      // Categoria mudou: zera a subcategoria para não ficar vínculo inconsistente.
      this.form.subcategoryId = null;
    },
    onAnswerEditorReady(quill) {
      // Guarda a instância do Quill para inserir a variável na posição do cursor.
      this.answerEditor = quill;
    },
    insertVariable(token) {
      // Insere o token no cursor do editor; se não houver seleção, anexa ao fim.
      const quill = this.answerEditor;
      if (quill) {
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertText(index, token, "user");
        quill.setSelection(index + token.length, 0);
        this.form.answer = quill.root.innerHTML;
      } else {
        this.form.answer = (this.form.answer || "") + token;
      }
    },
    close() {
      this.dialog = false;
    },
    save() {
      const payload = {
        id: this.form.id,
        question: this.form.question,
        answer: this.form.answer,
        isVisibleToDonor: this.form.isVisibleToDonor,
        sortOrder: this.form.sortOrder,
        faqCampaignTypeIds: this.form.faqCampaignTypeIds,
        subcategoryId: this.form.subcategoryId
      };
      this.knowledgeBaseFaqService.save(payload).then(() => {
        this.$emit("saved");
        this.close();
      });
    }
  }
};
</script>

<style scoped>
.kb-modal-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.kb-modal-body {
  padding: 0 !important;
}
.kb-form {
  width: 100%;
  box-sizing: border-box;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.kb-field {
  display: block;
  width: 100%;
  margin-bottom: 12px;
}
.kb-variables {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
}
.kb-variables-label {
  font-size: 12px;
  font-weight: 500;
  color: #555;
}
.kb-variables .v-chip {
  cursor: pointer;
}
.kb-variables-hint {
  font-size: 11px;
  color: #888;
  flex-basis: 100%;
}
.kb-field-row {
  display: flex;
  gap: 16px;
}
.kb-field-row .kb-field {
  flex: 1;
  min-width: 0;
}
.kb-field > label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
}
.kb-field /deep/ .input-comp,
.kb-field /deep/ .inside-input,
.kb-field /deep/ input,
.kb-field /deep/ textarea,
.kb-field /deep/ .v-input,
.kb-field /deep/ .v-text-field,
.kb-field /deep/ .v-input__slot,
.kb-field /deep/ .v-text-field__slot {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}
.kb-modal-actions {
  justify-content: flex-end;
  padding: 16px 24px;
}
</style>
